"use client";

import { useEffect, useState } from "react";
import { api, loadTokens } from "./api";
import type { MoneyCurrency } from "./format";
import { useRegion } from "./regionContext";

/**
 * EPIC-029 STORY-1701 PR 2 — resolved display currency hook.
 *
 * Returns the currency the UI should format money values in.
 * Resolution order:
 *   1. `users.display_currency` if the user has set an explicit
 *      override (per-user preference, persisted via STORY-1701 PR 1's
 *      PATCH /v1/me/display-currency endpoint).
 *   2. `region.currency` from the granbay.region cookie (region
 *      default — BRL for region=brazil, USD for region=global).
 *   3. `'USD'` as the conservative final fallback.
 *
 * Why a separate hook from useRegion: useRegion is cheap (cookie
 * read, no network). useDisplayCurrency optionally reaches /v1/me to
 * pull the per-user override, which is a network round-trip and only
 * needed for authed users on display surfaces. Splitting them keeps
 * useRegion fast for callers that just need locale, and keeps the
 * /v1/me fetch off the critical render path.
 *
 * For unauth'd users, this hook is a thin wrapper over useRegion's
 * currency — the network call is skipped entirely.
 *
 * Callers should use this for any money-display formatting in
 * authenticated surfaces. Pair with formatMoney() / formatMoneyCompact()
 * from `@/lib/format`.
 */
export function useDisplayCurrency(): MoneyCurrency {
  const region = useRegion();
  const [override, setOverride] = useState<MoneyCurrency | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!loadTokens()) {
      // Anonymous viewer — region currency is the answer; skip the
      // /v1/me fetch entirely.
      return;
    }
    void api
      .me()
      .then((profile) => {
        if (cancelled) return;
        const dc = profile.display_currency;
        if (dc && isValidCurrency(dc)) {
          setOverride(dc);
        } else {
          setOverride(null);
        }
      })
      .catch(() => {
        // Non-fatal — fall back to region currency.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return override ?? (region.currency as MoneyCurrency);
}

function isValidCurrency(s: string): s is MoneyCurrency {
  return (
    s === "BRL" ||
    s === "USD" ||
    s === "USDC" ||
    s === "MXN" ||
    s === "CAD" ||
    s === "INR"
  );
}
